import { useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import {
	ArrowRight,
	CalendarDays,
	LoaderCircle,
	Search,
	SearchX,
	UserPlus,
	UserRound,
	X,
} from "lucide-react";

import { orpc } from "@/client";
import type { DirectoryItem } from "@/api/db/people";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import { OverlayShell } from "@/components/ui/overlay";
import { useDebouncedSearch } from "@/hooks/use-debounced-search";
import { format } from "@/lib/format";
import { PessoasLabels } from "@/lib/labels/pessoas";

function AppSearchResult({ person, onClose }: { person: DirectoryItem; onClose: () => void }) {
	const name =
		format.readableName(person.name) ?? format.phone(person.phone) ?? PessoasLabels.unnamed;

	return (
		<li>
			<Link
				to="/pessoa/$id"
				params={{ id: person.id }}
				onClick={onClose}
				data-search-result
				className="flex min-h-18 items-center gap-3 rounded-md px-3 py-3 transition-colors hover:bg-secondary focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
			>
				<span className="grid size-10 shrink-0 place-items-center rounded-md bg-primary-soft text-primary">
					<UserRound className="size-4" />
				</span>
				<div className="min-w-0 flex-1">
					<p className="break-words text-sm font-semibold text-foreground">{name}</p>
					<div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground">
						<span>{PessoasLabels.link[person.link]}</span>
						{!!person.phone && <span className="font-mono">{format.phone(person.phone)}</span>}
					</div>
				</div>
				<ArrowRight className="size-4 shrink-0 text-muted-foreground" />
			</Link>
		</li>
	);
}

export function AppSearch({ onClose }: { onClose: () => void }) {
	const navigate = useNavigate();
	const panelRef = useRef<HTMLDivElement>(null);
	const [term, setTerm] = useState("");
	const [q, setQ] = useState("");
	useDebouncedSearch(term, setQ);

	const { data, isFetching, isError, refetch } = useQuery(
		orpc.people.directory.queryOptions({
			input: { q, page: 1, problems: false, include_non_human: false },
			enabled: q.length > 0,
		}),
	);
	const waiting = term.trim() !== q || (isFetching && !data);
	const showResults = term.trim().length > 0 && !waiting;

	return (
		<OverlayShell
			label="Buscar pessoas"
			onClose={onClose}
			className="items-center justify-center p-4"
		>
			<div
				ref={panelRef}
				className="overlay-panel max-w-xl"
				onKeyDown={(event) => {
					if (event.key !== "ArrowDown" && event.key !== "ArrowUp") {
						return;
					}

					const results = Array.from(
						panelRef.current?.querySelectorAll<HTMLAnchorElement>("[data-search-result]") ?? [],
					);

					if (results.length === 0) {
						return;
					}

					event.preventDefault();
					const current = results.indexOf(document.activeElement as HTMLAnchorElement);

					if (current < 0) {
						const first = event.key === "ArrowDown" ? 0 : results.length - 1;
						results[first].focus();

						return;
					}

					const next =
						event.key === "ArrowDown"
							? (current + 1) % results.length
							: (current - 1 + results.length) % results.length;
					results[next].focus();
				}}
			>
				<form
					onSubmit={(event) => {
						event.preventDefault();

						if (!term.trim()) {
							return;
						}

						navigate({ to: "/pessoas", search: { q: term.trim(), page: 1 } });
						onClose();
					}}
				>
					<div className="flex items-center gap-3 border-b border-border p-4 sm:p-5">
						<Search className="size-5 shrink-0 text-primary" />
						<input
							value={term}
							onChange={(event) => setTerm(event.target.value)}
							name="q"
							type="search"
							aria-label="Nome, CPF, telefone ou email"
							placeholder="Nome, CPF, telefone ou email"
							className="min-h-11 min-w-0 flex-1 rounded-md bg-transparent px-2 text-base text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
						/>
						<button
							type="button"
							onClick={onClose}
							aria-label="Fechar busca"
							className="control-button button-ghost size-11"
						>
							<X />
						</button>
					</div>
				</form>

				<div aria-live="polite" aria-busy={waiting} className="min-h-32">
					{term.trim().length > 0 && waiting && (
						<p className="flex items-center gap-2 p-5 text-sm text-muted-foreground">
							<LoaderCircle className="size-4 animate-spin" />
							Buscando pessoas...
						</p>
					)}
					{showResults && isError && (
						<div className="flex flex-col items-start gap-3 p-5">
							<p className="text-sm text-destructive">Não foi possível buscar pessoas.</p>
							<Button type="button" variant="outline" size="sm" onClick={() => refetch()}>
								Tentar novamente
							</Button>
						</div>
					)}
					{showResults && !isError && data?.items.length === 0 && (
						<EmptyState icon={SearchX} title="Nenhuma pessoa encontrada" />
					)}
					{showResults && !isError && !!data && data.items.length > 0 && (
						<>
							<ul
								aria-label="Pessoas encontradas"
								className="max-h-[min(26rem,50dvh)] overflow-y-auto p-2"
							>
								{data.items.map((person) => (
									<AppSearchResult key={person.id} person={person} onClose={onClose} />
								))}
							</ul>
							<Link
								to="/pessoas"
								search={{ q, page: 1 }}
								onClick={onClose}
								className="flex min-h-12 items-center justify-between gap-3 border-t border-border bg-background px-5 py-3 text-sm font-medium text-primary hover:bg-primary-soft focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
							>
								<span>Ver todos os resultados</span>
								<span className="text-xs tabular-nums">{data.total.toLocaleString("pt-BR")}</span>
							</Link>
						</>
					)}
					{term.trim().length === 0 && (
						<div className="p-3">
							<Link to="/cadastro" onClick={onClose} className="menu-item">
								<UserPlus className="size-4 text-primary" />
								Novo cadastro
								<ArrowRight className="ml-auto size-4 text-muted-foreground" />
							</Link>
							<Link to="/agenda" onClick={onClose} className="menu-item">
								<CalendarDays className="size-4 text-primary" />
								Abrir agenda
								<ArrowRight className="ml-auto size-4 text-muted-foreground" />
							</Link>
						</div>
					)}
				</div>
			</div>
		</OverlayShell>
	);
}
